import C from "@/admin/AdminLayout.module.css";
import MLC from "@/modules/main/MainLayout.module.css";
import TNC from "@/modules/main/TopNav.module.css";

import { sideNavCompactAtom } from "@/admin/AdminLayout.store.ts";
import { serializeQuickFilter } from "@/admin/filterUtils.ts";
import { type AdminMenuItemType, menuItemPrivilegeAtom, resourcePrivilegeAtom } from "@/admin/privileges.ts";
import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { getResourceIconClass, type ResourceType } from "@/modules/api/resourceTypes.tsx";
import { IsolatedChildren } from "@/modules/error/Isolated.tsx";
import { hasDebugBarAtom } from "@/modules/main/debug/DebugMenu.store.ts";
import { LayoutContent } from "@/modules/main/LayoutContent.tsx";
import { showOnlyContentAtom } from "@/modules/main/MainLayout.store.ts";
import { type BaseProps, mcn } from "@/utils/baseProps.ts";
import { cn } from "@/utils/classNames.ts";
import { type ActiveOptions, Link, type LinkProps, useMatchRoute } from "@tanstack/react-router";
import { useAtom, useAtomValue } from "jotai/react";
import { atom, type Atom } from "jotai/vanilla";
import React, { type ReactNode, Suspense, useMemo, useState } from "react";

const ColorSchemeMenu = React.lazy(() => import("@/modules/main/ColorSchemeMenu.tsx"));
const NotificationMenu = React.lazy(() => import("@/modules/notifications/NotificationMenu.tsx"));
const UserMenu = React.lazy(() => import("@/modules/user/UserMenu.tsx"));
const DebugButton = React.lazy(() => import("@/modules/main/debug/DebugButton.tsx"));
const DebugBar = React.lazy(() => import("@/modules/main/debug/DebugMenu.tsx"));
const CurrencyButton = React.lazy(() => import("@/modules/currency/CurrencyModal.tsx"));

export function AdminRootLayout({ children }: { children: ReactNode }) {
    //#region Hooks
    const showOnlyContent = useAtomValue(showOnlyContentAtom);
    //#endregion

    if (showOnlyContent) {
        return children;
    }

    return (
        <div className="size-full flex flex-col">
            <AdminTopNav />
            <div className={cn(MLC.mainArea)}>
                <AdminSideNav />
                {children}
            </div>
        </div>
    );
}

/** Scrollable pane for displaying main content */
export function AdminScrollPane({ children }: { children: ReactNode }) {
    return (
        <LayoutContent
            className={cn(
                "flex-grow overflow-y-auto contain-strict",
                // Needed for sticky headers
                "relative overflow-x-auto",
            )}
        >
            {children}
        </LayoutContent>
    );
}

function AdminTopNav(props: BaseProps) {
    const hasDebugBar = useAtomValue(hasDebugBarAtom);
    const header = (
        <header {...mcn(cn("HStack", TNC.TopNav, "contain-content"), props)}>
            <Logo />
            <Spacer />
            <IsolatedChildren>
                <CurrencyButton />
                <ColorSchemeMenu />
                <NotificationMenu />
                {hasDebugBar && <DebugButton />}
                <UserMenu />
            </IsolatedChildren>
        </header>
    );

    return (
        <>
            {header}
            {hasDebugBar && (
                <Suspense>
                    <DebugBar />
                </Suspense>
            )}
        </>
    );
}

function Logo() {
    return <Link to="/admin" aria-label="Home" className={`bg-icon ${TNC.logo} mr-2`} />;
}

interface MenuItem {
    id: string;
    title: string;
    icon: string;
    enabledAtom?: Atom<boolean>;
    to?: LinkProps["to"];
    search?: LinkProps["search"];
    childItems?: MenuItem[];
}

function resourceItem(resourceType: ResourceType) {
    return {
        icon: getResourceIconClass(resourceType),
        enabledAtom: resourcePrivilegeAtom(resourceType),
    } satisfies Partial<MenuItem>;
}

function menuItem(itemId: AdminMenuItemType) {
    return {
        enabledAtom: menuItemPrivilegeAtom(itemId),
    } satisfies Partial<MenuItem>;
}

const menuItemsDefinition: MenuItem[] = [
    {
        ...menuItem("dashboard"),
        id: "dashboard",
        title: "Dashboard",
        icon: "jp-view-apps-icon",
        to: "/admin",
    },
    {
        // Only show when VMs are enabled
        ...resourceItem("virtual_machine"),
        id: "compute",
        title: "Compute",
        icon: "jp-compute-cloud-icon",
        childItems: [
            {
                ...resourceItem("virtual_machine"),
                id: "virtual_machines",
                title: "Virtual Machines",
                to: "/admin/virtual_machines",
                search: {
                    filters: [
                        serializeQuickFilter({
                            field: "STATUS",
                            value: "running,stopping,stopped",
                        }),
                    ],
                }, // only show running VMs by default
            },
            {
                id: "hosts",
                title: "Hypervisors",
                icon: "jp-host-icon",
                to: "/admin/hosts",
            },
            {
                id: "host_pools",
                title: "Hypervisor Pools",
                icon: "jp-hostpool-icon",
                to: "/admin/host_pools",
            },
            {
                id: "storage_pools",
                title: "Storage Pools",
                icon: "jp-hostpool-icon",
                to: "/admin/storage_pools",
            },
        ],
    },
    {
        ...resourceItem("bare_metal"),
        id: "metal",
        title: "Bare metal",
        icon: "jp-host-icon",
        childItems: [
            {
                id: "metal/machines",
                title: "Machines",
                icon: "jp-host-icon",
                to: "/admin/metal/machines",
            },
            {
                id: "metal/specs",
                title: "Machine Types",
                icon: "jp-hostpool-icon",
                to: "/admin/metal/specs",
            },
            {
                id: "metal/os",
                title: "Operating Systems",
                icon: "jp-linux-icon",
                to: "/admin/metal/os",
            },
        ],
    },
    {
        id: "network",
        title: "Network",
        icon: "jp-network-icon",
        childItems: [
            {
                ...resourceItem("load_balancer"),
                id: "load_balancers",
                title: "Load Balancers",
                to: "/admin/load_balancers",
            },
            {
                ...resourceItem("ip_address"),
                id: "floating_ips",
                title: "IP addresses",
                to: "/admin/floating_ips",
            },
        ],
    },
    {
        ...resourceItem("bucket"),
        id: "buckets",
        title: "Object Storage",
        to: "/admin/buckets",
    },
    {
        ...resourceItem("managed_service"),
        id: "service_packages",
        title: "Services",
        to: "/admin/service_packages",
    },
    {
        // Only show when VMs are enabled
        ...resourceItem("virtual_machine"),
        id: "vm_images",
        title: "Images",
        icon: "jp-disc-icon",
        to: "/admin/vm_images",
    },
    {
        ...menuItem("users"),
        id: "users",
        title: "Users",
        icon: "jp-user-icon",
        childItems: [
            {
                id: "users",
                title: "Users",
                icon: "jp-user-group-icon",
                to: "/admin/users",
            },
            {
                ...menuItem("admin_users"),
                id: "admin_users",
                title: "Admin Users",
                icon: "jp-user-id-icon",
                to: "/admin/admin_users",
            },
        ],
    },
    {
        ...resourceItem("billing_account"),
        id: "accounts",
        title: "Billing Accounts",
        to: "/admin/accounts",
    },
    {
        ...resourceItem("billing_account"),
        id: "reports",
        title: "Invoices & Reports",
        icon: "jp-to-do-icon",
        to: "/admin/reports",
    },
    {
        ...menuItem("prices"),
        id: "prices",
        title: "Price Lists",
        icon: "jp-tags-icon",
        to: "/admin/prices",
    },
    {
        ...menuItem("custom_products"),
        id: "custom_products",
        title: "Custom Products",
        icon: "i-lucide:scan-barcode",
        to: "/admin/custom_products",
    },
    {
        ...menuItem("marketing"),
        id: "marketing",
        title: "Marketing",
        icon: "jp-broadcast-icon",
        childItems: [
            {
                id: "campaigns",
                title: "Campaigns",
                icon: "jp-ticket-icon",
                to: "/admin/campaigns",
            },
            {
                id: "referral",
                title: "Referral Program",
                icon: "jp-trophy-icon",
                to: "/admin/referral",
            },
        ],
    },
    {
        ...menuItem("locations"),
        id: "locations",
        title: "Locations",
        icon: "jp-web-icon",
        to: "/admin/locations",
    },
    {
        ...menuItem("settings"),
        id: "settings",
        title: "Settings",
        icon: "i-lucide:settings",
        childItems: [
            {
                id: "general",
                title: "General",
                icon: "jp-icon-toggles",
                to: "/admin/general",
            },
            {
                // Only show when VMs are enabled
                ...resourceItem("virtual_machine"),
                id: "vm_conf",
                title: "Network",
                icon: "jp-network-icon",
                to: "/admin/vm_conf",
            },
            {
                id: "tracking",
                title: "Tracking Codes",
                icon: "i-lucide:cookie",
                to: "/admin/tracking",
            },
            {
                id: "currencies",
                title: "Currencies",
                icon: "i-lucide:coins",
                to: "/admin/currencies",
            },
            // TODO: Hide behind a localstorage flag or user email?
            {
                id: "configuration",
                title: "Platform Configuration",
                icon: "i-lucide:settings",
                to: "/admin/configuration",
            },
            {
                id: "help",
                title: "Help",
                icon: "jp-help-icon",
                to: "/admin/help",
            },
        ],
    },
];

const menuItemsAtom = atom((get) =>
    menuItemsDefinition
        .filter(({ enabledAtom }) => !enabledAtom || get(enabledAtom))
        .map((item) => ({
            ...item,
            childItems: item.childItems?.filter(({ enabledAtom }) => !enabledAtom || get(enabledAtom)),
        }))
        // Hide empty sections
        .filter(({ childItems }) => childItems == null || childItems.length > 0),
);

const commonIconClassName = "size-0.875rem color-primary";

const linkActiveOptions: ActiveOptions = { exact: true, includeSearch: false };

function NavLink({ item: { icon, title, to, search }, ...props }: LinkProps & BaseProps & { item: MenuItem }) {
    return (
        <Link activeOptions={linkActiveOptions} to={to} search={search} {...mcn(C.SideNavButton, props)}>
            <MaskIcon className={`${icon} ${commonIconClassName}`} />
            <span className={C.Label}>{title}</span>
        </Link>
    );
}

function AdminSideNav(props: BaseProps) {
    //#region Hooks
    const matchRoute = useMatchRoute();
    const menuItems = useAtomValue(menuItemsAtom);

    // Check if any child items are currently active
    const activeParentIds = useMemo(
        () =>
            menuItems
                .filter((item) => item.childItems?.some(({ to }) => matchRoute({ ...linkActiveOptions, to })))
                .map((item) => item.id),
        [matchRoute, menuItems],
    );

    const [isCompact, setIsCompact] = useAtom(sideNavCompactAtom);
    const [openIds, setOpenIds] = useState<string[]>(activeParentIds);
    //#endregion

    const toggleOpen = (value: string) => {
        const isOpen = openIds.find((item) => item === value);
        if (isOpen) {
            setOpenIds(openIds.filter((item) => item !== value));
        } else {
            setOpenIds([...openIds, value]);
        }
    };

    const hasOpen = (value: string) => {
        return openIds.some((item) => item === value);
    };

    const menus = menuItems.map((item) => {
        const { title, icon, id, childItems } = item;
        if (childItems?.length) {
            const isOpen = hasOpen(id);
            const isActive = isOpen || activeParentIds.includes(id);
            return (
                <div key={id} className={cn(C.SideNavHolder, hasOpen(id) && C.isOpen)}>
                    <div className={cn(C.SideNavHeader, isActive && "color-primary")} onClick={() => toggleOpen(id)}>
                        <MaskIcon className={cn(icon, commonIconClassName)} />
                        <span className={C.Label}>{title}</span>
                        <MaskIcon
                            className={cn(
                                isOpen ? "i-lucide:chevron-up" : "i-lucide:chevron-down",
                                isCompact ? C.Icon : "size-0.875rem",
                                "color-primary",
                            )}
                        />
                    </div>

                    <div style={isOpen ? {} : { visibility: "hidden", height: "0" }}>
                        {childItems.map((child) => (
                            <NavLink key={child.id} item={child} />
                        ))}
                    </div>
                </div>
            );
        } else {
            return <NavLink key={id} item={item} />;
        }
    });

    return (
        <nav {...mcn(cn(C.SideNav, "contain-content", isCompact && C.Compact), props)}>
            {menus}

            <div className={C.Footer}>
                <WButton
                    color="primary"
                    size="bar"
                    variant="ghost"
                    icon={isCompact ? "i-lucide:chevron-right" : "i-lucide:chevron-left"}
                    action={() => setIsCompact(!isCompact)}
                />
            </div>
        </nav>
    );
}
